import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { CreditCard, RefreshCw, Search, Eye, FileText, Wallet, AlertTriangle, TrendingUp, CheckCircle2, XCircle, Plus } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { InvoiceStatusBadge, PaymentStatusBadge, AccountStatusBadge, formatIDR } from "./billing/BillingBadges";
import PaymentGatewayTab from "./billing/PaymentGatewayTab";

const TRIGGER = "bg-white/5 border-white/10 text-white hover:bg-white/10";
const CONTENT = "z-[110] bg-[#0F0F17] border-white/10 text-white";
const ITEM = "text-white focus:bg-white/10 focus:text-white";

const TABS = ["overview", "invoices", "payments", "accounts", "gateway", "analytics"];

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
}

function SummaryCard({ label, value, icon: Icon, tone = "neutral", testid, sub }) {
  const tones = {
    neutral: "text-white", emerald: "text-emerald-300", amber: "text-amber-300",
    rose: "text-rose-300", sky: "text-sky-300", indigo: "text-indigo-300",
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        {Icon ? <Icon size={14} className={tones[tone]} /> : null}
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{label}</div>
      </div>
      <div className={`text-xl md:text-2xl font-black font-display ${tones[tone]}`}>{value}</div>
      {sub ? <div className="text-[10px] text-white/40 mt-1">{sub}</div> : null}
    </div>
  );
}

export default function PlatformBilling() {
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [recentInv, setRecentInv] = useState([]);
  const [recentPay, setRecentPay] = useState([]);
  const [q, setQ] = useState("");
  const [invStatus, setInvStatus] = useState("");
  const [payStatus, setPayStatus] = useState("");
  const [loading, setLoading] = useState(true);

  // create-invoice form
  const [newTenantId, setNewTenantId] = useState("");
  const [newPlan, setNewPlan] = useState("STARTER");
  const [newCycle, setNewCycle] = useState("MONTHLY");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [root, inv, pay, acc] = await Promise.all([
        api.get("/platform/billing"),
        api.get("/platform/billing/invoices", { params: q || invStatus ? { q: q || undefined, status: invStatus || undefined } : {} }),
        api.get("/platform/billing/payments", { params: payStatus ? { status: payStatus } : {} }),
        api.get("/platform/billing/accounts"),
      ]);
      setSummary(root.data.summary);
      setRecentInv(root.data.recent_invoices || []);
      setRecentPay(root.data.recent_payments || []);
      setInvoices(inv.data.items || []);
      setPayments(pay.data.items || []);
      setAccounts(acc.data.items || []);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, [q, invStatus, payStatus]);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (id) => {
    try {
      await api.post(`/platform/billing/invoice/${id}/paid`, { note: "" });
      toast.success("Invoice ditandai PAID.");
      load();
    } catch (e) { notifyError(e); }
  };

  const voidInvoice = async (id) => {
    const reason = window.prompt("Alasan void invoice?");
    if (!reason || reason.length < 3) return;
    try {
      await api.post(`/platform/billing/invoice/${id}/void`, { reason });
      toast.success("Invoice di-void.");
      load();
    } catch (e) { notifyError(e); }
  };

  const createInvoice = async () => {
    if (!newTenantId.trim()) { toast.error("Tenant ID wajib diisi."); return; }
    setCreating(true);
    try {
      const r = await api.post("/platform/billing/invoice", {
        tenant_id: newTenantId.trim(), plan_code: newPlan, billing_cycle: newCycle,
      });
      toast.success(`Invoice ${r.data.invoice.invoice_number} dibuat.`);
      setNewTenantId("");
      load();
    } catch (e) { notifyError(e, "Gagal membuat invoice"); }
    finally { setCreating(false); }
  };

  return (
    <PlatformLayout title="Billing">
      <div data-testid="platform-billing-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
          <SummaryCard label="MRR" value={formatIDR(summary?.mrr)} icon={TrendingUp} tone="emerald" testid="bill-summary-mrr" sub={`${summary?.active_accounts ?? 0} active`} />
          <SummaryCard label="ARR" value={formatIDR(summary?.arr)} icon={TrendingUp} tone="emerald" testid="bill-summary-arr" />
          <SummaryCard label="Outstanding" value={formatIDR(summary?.outstanding)} icon={AlertTriangle} tone="amber" testid="bill-summary-outstanding" sub={`${summary?.unpaid_count ?? 0} pending`} />
          <SummaryCard label="Invoices" value={summary?.invoice_count ?? 0} icon={FileText} tone="sky" testid="bill-summary-invoices" sub={`${summary?.paid_count ?? 0} paid`} />
          <SummaryCard label="Payments" value={summary?.payment_count ?? 0} icon={Wallet} tone="indigo" testid="bill-summary-payments" sub={`Today: ${formatIDR(summary?.paid_today)}`} />
          <SummaryCard label="Suspended" value={summary?.suspended_count ?? 0} icon={XCircle} tone="rose" testid="bill-summary-suspended" sub={`${summary?.grace_count ?? 0} grace`} />
        </div>

        {/* Tabs */}
        <div className="mb-5 flex items-center gap-1 flex-wrap border-b border-white/10">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 text-xs uppercase font-bold tracking-wider border-b-2 -mb-px transition ${tab === t ? "border-emerald-400 text-emerald-300" : "border-transparent text-white/50 hover:text-white/80"}`}
              data-testid={`bill-tab-${t}`}>
              {t}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" onClick={load} disabled={loading} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="bill-reload">
              <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Reload
            </Button>
          </div>
        </div>

        {/* ==================== OVERVIEW ==================== */}
        {tab === "overview" && (
          <div className="grid gap-4 lg:grid-cols-2" data-testid="bill-panel-overview">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText size={14} className="text-sky-300" />
                <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Recent Invoices</div>
              </div>
              {recentInv.length === 0 ? (
                <div className="text-white/40 text-xs italic" data-testid="bill-overview-invoices-empty">Belum ada invoice.</div>
              ) : recentInv.slice(0, 10).map((i) => (
                <div key={i.invoice_id} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0" data-testid={`bill-overview-inv-${i.invoice_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono text-white truncate">{i.invoice_number}</div>
                    <div className="text-[10px] text-white/40 truncate">{i.tenant_name}</div>
                  </div>
                  <div className="text-xs font-bold text-white">{formatIDR(i.grand_total)}</div>
                  <InvoiceStatusBadge status={i.status} />
                </div>
              ))}
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Wallet size={14} className="text-indigo-300" />
                <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Recent Payments</div>
              </div>
              {recentPay.length === 0 ? (
                <div className="text-white/40 text-xs italic" data-testid="bill-overview-payments-empty">Belum ada pembayaran.</div>
              ) : recentPay.slice(0, 10).map((p) => (
                <div key={p.payment_id} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0" data-testid={`bill-overview-pay-${p.payment_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono text-white truncate">{p.invoice_number}</div>
                    <div className="text-[10px] text-white/40 truncate">{p.tenant_name} · {p.method}</div>
                  </div>
                  <div className="text-xs font-bold text-white">{formatIDR(p.amount)}</div>
                  <PaymentStatusBadge status={p.status} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ==================== INVOICES ==================== */}
        {tab === "invoices" && (
          <div data-testid="bill-panel-invoices">
            {/* Create Invoice */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Plus size={14} className="text-emerald-300" />
                <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Buat Invoice Baru</div>
              </div>
              <div className="grid md:grid-cols-4 gap-2">
                <Input value={newTenantId} onChange={(e) => setNewTenantId(e.target.value)} placeholder="Tenant ID"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="bill-new-tenant" />
                <Select value={newPlan} onValueChange={setNewPlan}>
                  <SelectTrigger className={TRIGGER} data-testid="bill-new-plan"><SelectValue /></SelectTrigger>
                  <SelectContent className={CONTENT}>
                    {["STARTER", "GROWTH", "ENTERPRISE"].map((p) => <SelectItem key={p} value={p} className={ITEM}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={newCycle} onValueChange={setNewCycle}>
                  <SelectTrigger className={TRIGGER} data-testid="bill-new-cycle"><SelectValue /></SelectTrigger>
                  <SelectContent className={CONTENT}>
                    {["MONTHLY", "SIX_MONTHS", "YEARLY"].map((c) => <SelectItem key={c} value={c} className={ITEM}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={createInvoice} disabled={creating} className="bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30" data-testid="bill-new-submit">
                  {creating ? "Membuat..." : "Buat Invoice"}
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="mb-3 flex items-center gap-2 flex-wrap">
              <div className="flex-1 min-w-[200px] relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari invoice_number / tenant_id…"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pl-9" data-testid="bill-invoices-search" />
              </div>
              <Select value={invStatus || "ALL"} onValueChange={(v) => setInvStatus(v === "ALL" ? "" : v)}>
                <SelectTrigger className={`w-40 ${TRIGGER}`} data-testid="bill-invoices-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent className={CONTENT}>
                  {["ALL", "PENDING", "PAID", "UNPAID", "OVERDUE", "VOID", "CANCELLED"].map((s) => (
                    <SelectItem key={s} value={s} className={ITEM}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            {invoices.length === 0 ? (
              <div className="text-white/40 text-sm p-8 rounded-2xl border border-white/10 text-center" data-testid="bill-invoices-empty">Belum ada invoice.</div>
            ) : (
              <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="bill-invoices-table">
                <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
                  <div className="col-span-3">Invoice</div>
                  <div className="col-span-2">Tenant</div>
                  <div className="col-span-1">Plan</div>
                  <div className="col-span-2">Amount</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-1">Issue</div>
                  <div className="col-span-1">Due</div>
                  <div className="col-span-1 text-right">Actions</div>
                </div>
                {invoices.map((i) => (
                  <div key={i.invoice_id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center" data-testid={`bill-inv-row-${i.invoice_id}`}>
                    <div className="col-span-3 min-w-0">
                      <Link to={`/platform/billing/tenant/${i.tenant_id}`} className="text-xs font-mono font-bold text-white hover:text-emerald-300 truncate block" data-testid={`bill-inv-number-${i.invoice_id}`}>{i.invoice_number}</Link>
                      <div className="text-[10px] text-white/40 font-mono truncate">{i.invoice_id}</div>
                    </div>
                    <div className="col-span-2 text-xs text-white truncate">{i.tenant_name}</div>
                    <div className="col-span-1 text-[10px] font-mono text-white/70">{i.plan_code}</div>
                    <div className="col-span-2 text-xs font-black font-display text-white">{formatIDR(i.grand_total)}</div>
                    <div className="col-span-1"><InvoiceStatusBadge status={i.status} /></div>
                    <div className="col-span-1 text-[10px] text-white/60">{fmt(i.issue_date)}</div>
                    <div className="col-span-1 text-[10px] text-white/60">{fmt(i.due_date)}</div>
                    <div className="col-span-1 flex gap-1 justify-end">
                      {i.status !== "PAID" && i.status !== "VOID" && i.status !== "CANCELLED" && (
                        <button onClick={() => markPaid(i.invoice_id)} className="w-7 h-7 rounded-lg grid place-items-center text-emerald-300 hover:bg-emerald-500/20" title="Mark Paid" data-testid={`bill-inv-paid-${i.invoice_id}`}>
                          <CheckCircle2 size={12} />
                        </button>
                      )}
                      {i.status !== "PAID" && i.status !== "VOID" && (
                        <button onClick={() => voidInvoice(i.invoice_id)} className="w-7 h-7 rounded-lg grid place-items-center text-rose-300 hover:bg-rose-500/20" title="Void" data-testid={`bill-inv-void-${i.invoice_id}`}>
                          <XCircle size={12} />
                        </button>
                      )}
                      <Link to={`/platform/billing/tenant/${i.tenant_id}`} className="w-7 h-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10" title="View" data-testid={`bill-inv-view-${i.invoice_id}`}>
                        <Eye size={12} />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== PAYMENTS ==================== */}
        {tab === "payments" && (
          <div data-testid="bill-panel-payments">
            <div className="mb-3 flex items-center gap-2 flex-wrap">
              <Select value={payStatus || "ALL"} onValueChange={(v) => setPayStatus(v === "ALL" ? "" : v)}>
                <SelectTrigger className={`w-40 ${TRIGGER}`} data-testid="bill-payments-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent className={CONTENT}>
                  {["ALL", "SUCCESS", "PENDING", "FAILED", "REFUNDED", "EXPIRED", "CANCELLED"].map((s) => (
                    <SelectItem key={s} value={s} className={ITEM}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {payments.length === 0 ? (
              <div className="text-white/40 text-sm p-8 rounded-2xl border border-white/10 text-center" data-testid="bill-payments-empty">Belum ada pembayaran.</div>
            ) : (
              <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="bill-payments-table">
                <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
                  <div className="col-span-3">Payment ID</div>
                  <div className="col-span-2">Invoice</div>
                  <div className="col-span-2">Tenant</div>
                  <div className="col-span-2">Method</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-1">Amount</div>
                  <div className="col-span-1">Date</div>
                </div>
                {payments.map((p) => (
                  <div key={p.payment_id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center" data-testid={`bill-pay-row-${p.payment_id}`}>
                    <div className="col-span-3 text-xs font-mono text-white truncate">{p.payment_id}</div>
                    <div className="col-span-2 text-xs font-mono text-white/70 truncate">{p.invoice_number}</div>
                    <div className="col-span-2 text-xs text-white truncate">{p.tenant_name}</div>
                    <div className="col-span-2 text-[10px] font-mono text-white/60">{p.method} / {p.provider}</div>
                    <div className="col-span-1"><PaymentStatusBadge status={p.status} /></div>
                    <div className="col-span-1 text-xs font-black text-white">{formatIDR(p.amount)}</div>
                    <div className="col-span-1 text-[10px] text-white/60">{fmt(p.transaction_time || p.created_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== ACCOUNTS ==================== */}
        {tab === "accounts" && (
          <div data-testid="bill-panel-accounts">
            {accounts.length === 0 ? (
              <div className="text-white/40 text-sm p-8 rounded-2xl border border-white/10 text-center" data-testid="bill-accounts-empty">Belum ada billing account. Buat invoice pertama untuk otomatis membuat account.</div>
            ) : (
              <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="bill-accounts-table">
                <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
                  <div className="col-span-3">Tenant</div>
                  <div className="col-span-2">Plan</div>
                  <div className="col-span-2">Cycle</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2">Next Billing</div>
                  <div className="col-span-1 text-right">View</div>
                </div>
                {accounts.map((a) => (
                  <div key={a.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center" data-testid={`bill-acc-row-${a.id}`}>
                    <div className="col-span-3 min-w-0">
                      <div className="text-xs font-bold text-white truncate">{a.tenant_name}</div>
                      <div className="text-[10px] text-white/40 font-mono truncate">{a.tenant_id}</div>
                    </div>
                    <div className="col-span-2 text-xs font-mono text-white">{a.plan_code}</div>
                    <div className="col-span-2 text-xs font-mono text-white/70">{a.billing_cycle}</div>
                    <div className="col-span-2"><AccountStatusBadge status={a.status} /></div>
                    <div className="col-span-2 text-[10px] text-white/60">{fmt(a.next_billing_date)}</div>
                    <div className="col-span-1 flex justify-end">
                      <Link to={`/platform/billing/tenant/${a.tenant_id}`} className="w-7 h-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10" data-testid={`bill-acc-view-${a.id}`}>
                        <Eye size={12} />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== ANALYTICS ==================== */}
        {tab === "analytics" && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="bill-panel-analytics">
            <SummaryCard label="MRR" value={formatIDR(summary?.mrr)} icon={TrendingUp} tone="emerald" testid="bill-analytics-mrr" />
            <SummaryCard label="ARR" value={formatIDR(summary?.arr)} icon={TrendingUp} tone="emerald" testid="bill-analytics-arr" />
            <SummaryCard label="Paid This Month" value={formatIDR(summary?.paid_month)} icon={Wallet} tone="sky" testid="bill-analytics-paid-month" />
            <SummaryCard label="Paid Today" value={formatIDR(summary?.paid_today)} icon={Wallet} tone="sky" testid="bill-analytics-paid-today" />
            <SummaryCard label="Active Accounts" value={summary?.active_accounts ?? 0} icon={CreditCard} tone="emerald" testid="bill-analytics-active" />
            <SummaryCard label="Grace Period" value={summary?.grace_count ?? 0} icon={AlertTriangle} tone="amber" testid="bill-analytics-grace" />
            <SummaryCard label="Suspended" value={summary?.suspended_count ?? 0} icon={XCircle} tone="rose" testid="bill-analytics-suspended" />
            <SummaryCard label="Overdue Invoices" value={summary?.overdue_count ?? 0} icon={AlertTriangle} tone="rose" testid="bill-analytics-overdue" />
          </div>
        )}

        {/* ==================== GATEWAY (Payment Gateway) ==================== */}
        <PaymentGatewayTab tab={tab} />
      </div>
    </PlatformLayout>
  );
}
